<template>
  <div class="relative">
    <div class="-mb-2">
      <div class="flex flex-col -mx-4 mb-6">
        <FilterBarType
          v-show="!hasPredefinedAirport && showFilterBarType"
          ref="filterBarType"
          class="w-12/12 order-1 px-4"
          :class="{'flex-col': vertical }"
          :airports="options?.airports || []"
          :dark-filter="darkFilter"
          :vertical="vertical"
          is-only-flight-button
          @filter="$emit('filter')"
        />
      </div>
      <div class="-mx-2 mb-10 flex flex-wrap">
        <client-only>
          <div
            v-if="showLastMinute"
            class="p-2"
            :class="vertical ? 'w-full' : 'max-lg:w-full'"
          >
            <button
              class="btn rounded-lg py-2 px-4 font-normal normal-case flex flex-row items-center mr-2 min-w-[170px] justify-center w-full font-secondary"
              data-filter="only-lastminute"
              :class="filterLastMinute ? 'bg-blue-800 text-white' : 'bg-white !border !border-solid !border-black/20 text-black md:hover:bg-secondary md:hover:!text-white'"
              @click="toggleLastMinute"
            >
              <i-discounted class="inline" />
              <span
                class="block sm:inline-block ml-2 text-base"
                data-i18n="calendar.onlyLastMinute"
              >{{ $t('calendar.onlyLastMinute') }}</span>
            </button>
          </div>

          <div
            v-if="isSingleFinderActive && !isMap"
            class="p-2"
            :class="vertical ? 'w-full' : 'max-lg:w-full'"
          >
            <button
              class="btn rounded-lg py-2 px-4 font-normal normal-case flex flex-row items-center mr-2 min-w-[170px] justify-center w-full font-secondary disabled:opacity-75 disabled:pointer-events-none"
              data-filter="only-singlefinders"
              :disabled="disabledFilters.includes('singlefinder')"
              :class="[
                filters.singleFinder && !disabledFilters.includes('singlefinder')
                  ? 'bg-secondary text-white'
                  : 'bg-white !border !border-solid !border-black/20 text-black hover:bg-secondary/75 hover:!text-white',
              ]"
              @click="toggleOnlySinglefinders"
            >
              <span
                class="block sm:inline-block ml-0 sm:ml-2 text-base"
                data-i18n="memberShowOnlyCompanions"
              >{{ $t('memberShowOnlyCompanions') }}</span>
            </button>
          </div>
        </client-only>
      </div>
    </div>

    <div class="flex flex-wrap -mx-4 @container">
      <div class="filter-section flex flex-wrap w-full -mb-4">
        <div
          class="order-4 w-full px-4 dropdown-calendar"
          :class="getDropdownColumnClass"
        >
          <CalendarFilterDropdown
            ref="calendarDropdown"
            :disabled="disabledFilters.includes('fromdate')"
            data-filter="calendar"
            :close-on-click="false"
            :full-width="true"
            :is-active="!!filters.fromdate"
            :hide-icon="vertical"
            :value="displayFilterDate"
            icon="calendar"
            :label="$t('calendar.chooseDepartureDate')"
            label-i18n="calendar.chooseDepartureDate"
            class="w-full"
          >
            <LazyDatePicker
              :model-value="{ selectedDate: filters.fromdate }"
              :date-format="SERVER_DATE_FORMAT"
              @dayclick="calendarChoseDay"
            />
          </CalendarFilterDropdown>
        </div>
        <div
          v-if="!hasPredefinedAirport"
          class="order-5 w-full px-4"
          :class="getDropdownColumnClass"
        >
          <CalendarFilterDropdown
            ref="airportDropdown"
            :disabled="disabledFilters.includes('airport')"
            data-filter="airport"
            :is-active="filterTripType !== 'all' && filters.airport !== 'all'"
            :value="displayFilterFlight"
            icon="plane"
            :label="$t('calendar.chooseAirport')"
            label-i18n="calendar.chooseAirport"
            :hide-icon="vertical"
            class="w-full"
          >
            <button
              v-for="(airport, index) in airportList"
              :key="index"
              class="dropdown-item"
              @click="setAirport(airport)"
            >
              <span>
                {{ airport.name }}
              </span>
            </button>
          </CalendarFilterDropdown>
        </div>
        <div
          v-if="!hideRegionsFilter"
          class="order-6 w-full px-4"
          :class="getDropdownColumnClass"
        >
          <CalendarFilterDropdown
            :is-active="!!filters.regions.length"
            :value="displayFilterRegions"
            :close-on-click="false"
            :disabled="disabledFilters.includes('regions')"
            icon="earth-africa"
            :label="$t('calendar.chooseRegion')"
            label-i18n="calendar.chooseRegion"
            :hide-icon="vertical"
            class="w-full"
          >
            <div class="pl-4 pr-12 py-1">
              <input
                id="region-filter-all"
                class="charter-checkbox"
                type="checkbox"
                value="all"
                :checked="filters.regions.length === 0"
                @input="setFilterRegion(null, toggle)"
              />
              <label
                for="region-filter-all"
                data-i18n="calendar.allRegions"
              >{{ $t('calendar.allRegions') }}</label>
            </div>
            <div
              v-for="(region, index) in options.regions"
              :key="index"
              class="pl-4 pr-12 py-1"
            >
              <input
                :id="`region-filter-${region.id}`"
                class="charter-checkbox"
                type="checkbox"
                value="all"
                :checked="filters.regions.includes(region.category_url)"
                @input="setFilterRegion(region, toggle)"
              />
              <label
                class="clickable"
                :for="`region-filter-${region.id}`"
              >{{ region.rubrik }}</label>
            </div>
          </CalendarFilterDropdown>
        </div>

        <div
          v-if="showActivities"
          class="order-6 px-4 w-full"
          :class="[getDropdownColumnClass, { 'mt-0 md:mt-2': !hideRegionsFilter }]"
        >
          <CalendarFilterDropdown
            :is-active="!!filters.activities.length"
            :disabled="disabledFilters.includes('activities')"
            data-filter="activity"
            :value="displayFilterActivity"
            :label="$t('triptypes')"
            label-i18n="triptypes"
            :hide-icon="vertical"
            class="w-full"
          >
            <template #icon>
              <i-person-hike-spruce-snowing
                :font-controlled="false"
                class="scale-50"
              />
            </template>
            <button
              v-for="(activity, index) in activitiesList"
              :key="index"
              class="dropdown-item"
              @click="setFilterActivity(activity)"
            >
              <span>
                {{ activity.rubrik }}
              </span>
            </button>
          </CalendarFilterDropdown>
        </div>

        <div
          class="order-7 px-4 w-full mx-2"
          :class="[
            getDropdownColumnClass,
            { 'pb-10': vertical }
          ]"
        >
          <CalendarFilterTriplength
            ref="durationsDropdown"
            :disabled="disabledFilters.includes('duration')"
            data-filter="duration"
            :value="filterDuration"
            :label="$t('calendar.chooseTripLength')"
            label-i18n="calendar.chooseTripLength"
            :hide-icon="vertical"
            class="w-full"
            @change="setFilterDuration"
          />
        </div>
      </div>
      <ScreenWidthProvider
        v-slot="{ isWider }"
        class="refresh-filters flex flex-col md:flex-row justify-between items-center w-full mt-8 md:mt-3 px-4"
      >
        <client-only>
          <CalendarFilterButton
            v-if="!hasPredefinedAirport && isRolfSwe && viewMode !== 'map' && isAnyFilterActive"
            class="underline text-blue-800 font-semibold bg-white min-[600px]:mt-0 mt-8 p-2 md:py-2 md:px-4 rounded-lg w-full md:w-auto whitespace-nowrap"
            :label="$t('calendar.tooManyFiltersLink')"
            label-i18n="calendar.tooManyFiltersLink"
            icon="rotate-left"
            :icon-side="isWider('md') ? 'left' : 'right'"
            @click="resetFilters"
          />
        </client-only>
        <div
          v-if="showViewMode"
          class="w-full my-4 md:my-0"
        >
          <slot name="viewModeToggle" />
        </div>
      </ScreenWidthProvider>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { faGlobe } from '@fortawesome/pro-solid-svg-icons'
import { faCalendar, faPlane } from '@fortawesome/pro-duotone-svg-icons'
import { TRIP_TYPES } from '@packages/trip/constants/types.ts'
import { SERVER_DATE_FORMAT } from '@packages/date/formats'

export default defineNuxtComponent({
  async setup () {
    const { urls: localeURLs } = useLocale()
    const { localeDateFormat, localeDate } = useDate()
    const { trackClick } = useTracking()

    const { isSingleFinderActive } = await useSingleFinder()

    return {
      localeURLs,
      trackClick,
      localeDateFormat,
      localeDate,
      isSingleFinderActive,
    }
  },

  props: {
    viewMode: {
      type: String,
      default: 'list',
    },

    showViewMode: {
      type: Boolean,
      default: true,
    },

    showLastMinute: {
      type: Boolean,
      default: true,
    },

    showActivities: {
      type: Boolean,
      default: true,
    },

    vertical: {
      type: Boolean,
      default: false,
    },

    darkFilter: {
      type: Boolean,
      default: false,
    },

    hasPredefinedAirport: {
      type: Boolean,
      default: false,
    },

    showFilterBarType: {
      type: Boolean,
      default: true,
    },

    currentCategoryId: {
      type: Number,
      default: null,
    },

    activitiesOptionsOverride: {
      type: Array,
      default: null,
    },

    disabledFilters: {
      type: Array,
      default: [],
    },
  },

  emits: ['filter', 'resetFilters', 'update'],

  data () {
    return {
      window: 0,

      calendarKey: 0,

      icons: {
        faGlobe,
        faPlane,
        faCalendar,
      },

      SERVER_DATE_FORMAT,
    }
  },

  computed: {
    ...mapState(useLocaleStore, ['isRolfSwe']),
    ...mapState(useCalendarStore, ['isAnyFilterActive']),
    ...mapState(useCalendarStore, {
      data: 'calendarInit',
      options: 'calendarOptions',
      filters: 'filters',
      filterTripType: 'filterTripType',
      filterDuration: 'filterDuration',
      filterLastMinute: 'filterLastMinute',
    }),

    ...mapState(useLocaleStore, ['locale']),

    isMap () {
      return this.viewMode === 'map'
    },

    shouldDisplayTriptypeFilters () {
      if (this.locale !== 'no') {
        return true
      }

      return false
    },

    calendarsCount () {
      if (this.window > 1599) {
        return 3
      }

      return this.window >= 768 ? 2 : 1
    },

    displayFilterDate () {
      const dateFromFilter = this.filters.fromdate
        ? this.localeDate(this.filters.fromdate)
        : null

      return dateFromFilter || this.$t('allDates')
    },

    displayFilterRegions () {
      if (this.filters.regions.length === 0) {
        return this.$t('allRegions')
      }

      return this.filters.regions
        .map(r => this.options.regions.find(or => r === or.category_url).rubrik)
        .join(', ')
    },

    displayFilterFlight () {
      if (this.filterTripType === TRIP_TYPES.flight) {
        if (this.filters?.airport && this.filters.airport !== TRIP_TYPES.all) {
          const ap = this.airportList.find(o => o.iata === this.filters.airport)
          return ap?.name || this.$t('allAirports')
        }
      }
      return this.$t('allAirports')
    },

    airportList () {
      return (this.options.airports || [])
        .map(x => ({
          ...x,
          iata: x?.iata || x?.code || x?.value,
          name: x?.name,
        }))
        .filter(x => x?.iata)
    },

    activitiesOptions () {
      return this.activitiesOptionsOverride ?? this.options.activities
    },

    activitiesList () {
      let activitiesList = [
        { id: 'all', rubrik: this.$t('allTripTypes') },
        ...(this.activitiesOptions || []),
      ]

      if (this.currentCategoryId !== null) {
        activitiesList = activitiesList.filter((activity) => activity.id !== this.currentCategoryId)
      }

      return activitiesList
    },

    displayFilterActivity () {
      if (this.filters.activities.length > 0) {
        return this.filters.activities[0]
      }

      return this.$t('allTripTypes')
    },

    getDropdownColumnClass () {
      const classes = []

      if (this.vertical) {
        classes.push('w-12/12 mb-4')
      } else {
        classes.push('w-6/12 mb-10')

        if (this.hasPredefinedAirport) {
          classes.push('@[600px]:w-6/12 @[850px]:w-4/12')
        } else {
          classes.push('@[600px]:w-6/12 @[850px]:w-4/12')
        }
      }

      return classes.join(' ')
    },

    dropdownVariant () {
      if (this.darkFilter) {
        return 'dark-filter'
      }

      return 'modern'
    },

    hideRegionsFilter () {
      return (
        this.currentCategoryId !== null &&
        this.options.regions.some((region) => region.id === this.currentCategoryId)
      )
    },
  },

  watch: {
    window () {
      this.calendarKey++
    },

    '$route.query.lastminute' () {
      this.setLastMinute(!!parseInt(this.$route.query.lastminute))

      this.$emit('filter')
    },
  },

  mounted () {
    this.window = window.innerWidth

    window.addEventListener('resize', this.resizeListener)
  },

  beforeUnmount () {
    window.removeEventListener('resize', this.resizeListener)
  },

  methods: {
    ...mapActions(useCalendarStore, {
      setLastMinute: 'SET_LASTMINUTE',
    }),

    ...mapActions(useCalendarStore, [
      'setFilter',
      'setSelectedStartDate',
      'setDuration',
    ]),

    setFilterActivity (activity, toggle) {
      if (activity.id === 'all') {
        this.filters.activities = []
      } else {
        this.filters.activities = [activity.rubrik]
        this.setFilter({
          filter: 'category_url',
          value: [activity.category_url],
        })
      }
      toggle?.()
      this.$emit('filter')
    },

    toggleLastMinute () {
      if (this.$route.path === this.localeURLs.map) {
        this.$router.push({ query: { ...this.$route.query, lastminute: this.filterLastMinute ? 0 : 1 } }).catch(() => { })
      } else {
        this.$router.push({
          path: this.localeURLs[this.filterLastMinute ? 'calendar' : 'calendar-lastminute'],
          query: { ...this.$route.query },
        }).catch(() => { })
      }

      if (!this.data.google_analytics?.transaction) {
        return
      }

      this.trackClick({
        button: 'calendarLastMinute',
        active: this.filterLastMinute,
      })
    },

    toggleOnlySinglefinders () {
      this.setFilter({ filter: 'singleFinder', value: !this.filters.singleFinder ? 1 : 0 })
      this.$emit('filter')

      if (!this.data.google_analytics?.transaction) {
        return
      }

      this.trackClick({
        button: 'calendarOnlyCompanions',
        active: this.isSingleFinderActive,
      })
    },

    resizeListener () {
      this.window = window.innerWidth
    },

    setFilterRegion (region, toggle) {
      if (!region) {
        this.setFilter({ filter: 'regions', value: [] })
      } else {
        const i = this.filters.regions.findIndex(a => a === region.category_url)
        const regions = [...this.filters.regions]

        if (i > -1) {
          regions.splice(i, 1)
        } else {
          regions.push(region.category_url)
        }

        this.setFilter({
          filter: 'regions',
          value: regions,
        })
      }

      toggle?.()
      this.$emit('filter')
    },

    setFilterDuration (min, max) {
      this.setDuration({ min, max })

      this.setFilter({ filter: 'daysmin', value: min })
      this.setFilter({ filter: 'daysmax', value: max })

      this.$emit('filter')
    },

    calendarChoseDay ({ dateRaw: date }) {
      this.$refs.calendarDropdown.hide()

      this.setFilter({ filter: 'fromdate', value: this.$dayjs(date).format('YYYY-MM-DD') })
      this.setFilter({ filter: 'untildate', value: this.$dayjs(date).add(1, 'year').format('YYYY-MM-DD') })

      this.$emit('filter')
    },

    setAirport (airport) {
      if (!this.$refs.filterBarType) {
        return
      }

      this.$refs.filterBarType.setFilterFlight(airport.iata)
    },

    resetFilters () {
      this.$emit('resetFilters')
    },
  },
})
</script>

<style lang="scss">
@import "@layers/web/assets/scss/modules/charter/_flight-box";
@import "@layers/web/assets/scss/pages/_calendar";
.region-filter-dropdown {
  .v-popper__innerx {
    overflow-y: scroll;
    max-height: 400px;
    @media (max-height: 500px) {
      max-height: 300px;
    }
    @media (max-width: 650px) {
      max-height: 250px;
    }
  }
}
.dropdown-calendar {
  .vc-container {
    width: 100% !important;
  }
  .vc-day {
    @media (max-width: 600px) {
      padding-bottom: 1.5rem;
    }
    @media (max-width: 460px) {
      padding-bottom: 1rem;
    }
  }
  .vc-day-content {
    @media (max-width: 600px) {
      font-size: 1.2rem;
    }
  }
  .vc-day-layer {
    @media (max-width: 600px) {
      top: -20%;
      .vc-highlight {
        padding: 1rem;
      }
    }
    @media (max-width: 460px) {
      top: -15%;
    }
  }
}
</style>
